import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  Box,
  Divider,
  ListItem,
  Popover,
  PopoverAnchor,
  PopoverBody,
  PopoverContent,
  Portal,
  UnorderedList,
} from '@frontend/legacy-components';
import { constants } from '@frontend/partviewer';

import {
  cadViewer,
  viewerInteractions,
} from 'modules/common/viewer/cadViewer/data/actions';
import { getIsInteractionReady } from 'modules/common/viewer/cadViewer/data/reducer';

import type { CadPopoverNodeData } from './';

const { FORCE_VIEW_TYPES } = constants;

const PopoverButton = ({
  title,
  onClick,
}: {
  title: string;
  onClick: (e: React.MouseEvent<HTMLDivElement | HTMLButtonElement>) => void;
}) => {
  return (
    <Box
      as={'button'}
      outline="0"
      p="8px 12px"
      textAlign="left"
      transition="all 0.2s ease"
      w="100%"
      _hover={{ bg: '#ecf6fd', cursor: 'pointer' }}
      _focus={{ bg: '#d2eafb' }}
      _active={{ bg: '#d2eafb' }}
      onClick={onClick}
    >
      {title}
    </Box>
  );
};

type CadTreeContextMenuPopoverProps = {
  popoverNodeData: CadPopoverNodeData | null;
  activeRootBodyId: string | null;
  handleCloseMenu: () => void;
};

export const CadTreeContextMenuPopover = ({
  popoverNodeData,
  activeRootBodyId,
  handleCloseMenu,
}: CadTreeContextMenuPopoverProps) => {
  const { viewerId, rootId, viewerPartOccurances, isAssemblyOrSubAssembly } =
    popoverNodeData?.node ?? {};
  const instanceCount = viewerPartOccurances?.length || 1;

  const dispatch = useDispatch();
  const isInteractionReady = useSelector(getIsInteractionReady);

  const onFocusAllInstances = useCallback(() => {
    if (!isInteractionReady) return;
    dispatch(
      viewerInteractions.focusOnBodies({
        viewerBodyIds: viewerPartOccurances,
      }),
    );
    handleCloseMenu();
  }, [dispatch, handleCloseMenu, isInteractionReady, viewerPartOccurances]);

  // Focuses the Instance's Root Body on call of "Show All Bodies"
  const onFocusInstanceRoot = useCallback(() => {
    if (!isInteractionReady) return;
    dispatch(viewerInteractions.focusOnBodies({ viewerBodyIds: [rootId] }));
    handleCloseMenu();
  }, [dispatch, handleCloseMenu, isInteractionReady, rootId]);

  const onFocusInstance = useCallback(() => {
    if (!isInteractionReady) return;
    dispatch(viewerInteractions.focusOnBodies({ viewerBodyIds: [viewerId] }));
    handleCloseMenu();
  }, [dispatch, handleCloseMenu, isInteractionReady, viewerId]);

  const onIsolate = useCallback(() => {
    if (!isInteractionReady) return;
    if (activeRootBodyId !== rootId) {
      dispatch(viewerInteractions.focusOnBodies({ viewerBodyIds: [rootId] }));
    }
    dispatch(viewerInteractions.isolateBodies({ viewerBodyIds: [viewerId] }));
    handleCloseMenu();
  }, [
    dispatch,
    handleCloseMenu,
    isInteractionReady,
    viewerId,
    rootId,
    activeRootBodyId,
  ]);

  const onZoomToFit = useCallback(() => {
    if (!isInteractionReady) return;
    if (activeRootBodyId !== rootId) {
      dispatch(viewerInteractions.focusOnBodies({ viewerBodyIds: [rootId] }));
    }
    dispatch(cadViewer.appendVisibleBodies({ visibleBodies: [viewerId] }));
    dispatch(
      cadViewer.receiveForceView({
        forceView: {
          type: FORCE_VIEW_TYPES.ZOOM_TO_FIT,
          payload: { viewerId },
        },
      }),
    );
    handleCloseMenu();
  }, [
    dispatch,
    handleCloseMenu,
    isInteractionReady,
    viewerId,
    rootId,
    activeRootBodyId,
  ]);

  return (
    <Popover
      isOpen={!!popoverNodeData}
      onClose={handleCloseMenu}
      size="sm"
      placement="right-start"
      hasArrow
    >
      <Portal>
        <PopoverAnchor>
          <Box
            position="absolute"
            top={`${popoverNodeData?.popoverPosition?.top ?? 0}px`}
            left={`${popoverNodeData?.popoverPosition?.left ?? 0}px`}
          />
        </PopoverAnchor>
        <PopoverContent w="auto">
          <PopoverBody px={0} py={0.5}>
            <UnorderedList ms={0} listStyleType="none">
              <ListItem>
                <PopoverButton
                  title="Zoom To Fit"
                  onClick={(e) => {
                    e.stopPropagation();
                    onZoomToFit();
                  }}
                />
              </ListItem>
              <Divider my={0.5} />
              <ListItem>
                <PopoverButton
                  title="Focus"
                  onClick={(e) => {
                    e.stopPropagation();
                    onFocusInstance();
                  }}
                />
              </ListItem>
              {instanceCount > 1 && (
                <ListItem>
                  <PopoverButton
                    title={`Focus All Instances (${instanceCount})`}
                    onClick={(e) => {
                      e.stopPropagation();
                      onFocusAllInstances();
                    }}
                  />
                </ListItem>
              )}
              {isAssemblyOrSubAssembly && <Divider my={0.5} />}
              {isAssemblyOrSubAssembly && (
                <ListItem>
                  <PopoverButton
                    title="Isolate"
                    onClick={(e) => {
                      e.stopPropagation();
                      onIsolate();
                    }}
                  />
                </ListItem>
              )}
              {isAssemblyOrSubAssembly && (
                <ListItem mb="0">
                  <PopoverButton
                    title="Show All Bodies"
                    onClick={(e) => {
                      e.stopPropagation();
                      onFocusInstanceRoot();
                    }}
                  />
                </ListItem>
              )}
            </UnorderedList>
          </PopoverBody>
        </PopoverContent>
      </Portal>
    </Popover>
  );
};
